import { NavLink, withRouter } from 'react-router-dom'
import React from 'react'
import { OperationModeIcon } from '../../root/components/common/operation-mode-icon'
import { TableVirtualized } from '../../root/components/common/table-virtualized/table'
import Icon from '@mdi/react'
import { mdiQrcodeScan, mdiTools } from '@mdi/js'
import {
    getBatteryPercentage,
    getDeviceShortStatusText,
    getDeviceStatusTooltipText,
    getFormattedDate
} from '../../../utils'
import {
    Battery,
    Checkbox,
    DeviceStatus,
    IsAssignedIcon,
    Tooltip,
    WifiStrengthIndicator
} from '../../root/components/common'
import { useSelector } from 'react-redux'
import { makeStyles, IconButton, Typography } from '@material-ui/core'
import BackspaceIcon from '@material-ui/icons/BackspaceOutlined'
import { Highlighted } from '../../root/components/common/highlighted'
import classNames from 'classnames'
import { UserPermissions } from 'web/src/services/permissions'
import { formatHeartbeatTimestamp } from 'web/src/utils/last-heartbeat'
import { TRANSFER_MODE } from '../../root/constants'

// Fits the longest translated system name at the table's 13px font size.
const PEST_CONTROL_SYSTEM_COLUMN_WIDTHS = {
    en: 200,
    de: 185,
    cs: 175,
    fr: 245,
    nl: 210,
    es: 265,
    pt: 280
}

const useStyles = makeStyles(theme => ({
    disabled: {
        '&&': {
            color: theme.quaternary
        }
    },
    disabledImage: {
        filter: 'opacity(0.2)'
    },
    nameLink: {
        ...theme.link
    },
    batteryWrapper: {
        alignItems: 'center',
        display: 'flex'
    },
    wifiWrapper: {
        display: 'flex',
        flex: 1,
        justifyContent: 'flex-end'
    },
    lastColumn: {
        display: 'flex',
        flex: 1,
        justifyContent: 'flex-end'
    },
    statusWrapper: {
        display: 'flex',
        justifyContent: 'center'
    },
    qrIcon: {
        cursor: 'pointer',
        '&:hover': {
            opacity: 0.3
        }
    },
    restoreIconButton: {
        color: theme.primary
    },
    deviceStatusShortText: {
        marginLeft: 4
    },
    assignmentRowBlocker: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: theme.background,
        opacity: 0.5
    },
    checkboxOverride: {
        marginRight: '0px !important'
    }
}))

const DevicesTableComponent = ({
    embeded,
    onQrIconClick,
    devices,
    isLoading,
    isLoadingMore,
    onLoadMore,
    isDevicesToMoveMode,
    onRestoreDevice,
    sorting,
    onSort,
    showAssignmentColumn,
    search,
    history,
    multiselect
}) => {
    const {
        theme,
        locale,
        localeCode,
        user: { original_role }
    } = useSelector(state => state.main)

    const classes = useStyles()
    const pestControlSystemWidth =
        PEST_CONTROL_SYSTEM_COLUMN_WIDTHS[localeCode] || 280

    const Link = embeded
        ? prop => <NavLink target={'_blank'} {...prop} />
        : NavLink

    const isDisabledRow = isAssigned => {
        return !isAssigned && !UserPermissions.isAllOriginalGranted()
    }

    const handleLinkClick = e => {
        e.stopPropagation()
    }

    const columns = [
        ...(multiselect && !isDevicesToMoveMode
            ? [
                  {
                      key: 'checkbox',
                      label: '',
                      sortable: false,
                      dataType: 'string',
                      width: 0.5,
                      maxWidth: 38,
                      minWidth: 38,
                      paddingLeft: 10,
                      alignCenter: true,
                      render: ({ rowData }) => {
                          return (
                              <Checkbox
                                  className={classes.checkboxOverride}
                                  customOnClick={e => {
                                      e.stopPropagation()
                                  }}
                                  onClick={(e, selected) => {
                                      multiselect.selectRow(
                                          rowData.id,
                                          selected
                                      )
                                  }}
                                  checked={multiselect.isRowSelected(
                                      rowData.id
                                  )}
                              />
                          )
                      },
                      headerRenderer: () => {
                          return (
                              <div style={{ paddingLeft: 10 }}>
                                  <Checkbox
                                      className={classes.checkboxOverride}
                                      customOnClick={e => {
                                          e.stopPropagation()
                                      }}
                                      indeterminate={
                                          multiselect.isAllCheckboxIndeterminate
                                      }
                                      onClick={multiselect.toggleAllSelector}
                                      checked={multiselect.isAllSelectorOn}
                                  />
                              </div>
                          )
                      }
                  }
              ]
            : []),
        {
            key: 'mode',
            label: '',
            sortable: false,
            dataType: 'string',
            width: 0.5,
            maxWidth: 60,
            minWidth: 60,
            alignCenter: true,
            render: ({ rowData, cellData }) => {
                const isAssigned = rowData.is_assigned
                if (rowData.hasOwnProperty('transfer_mode')) {
                    return (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}>
                            <OperationModeIcon
                                operationMode={rowData.operation_mode}
                                locale={locale}
                                size="small"
                            />
                            <div
                                style={{
                                    fontSize: 12
                                }}>
                                {rowData.transfer_mode === TRANSFER_MODE.wifi
                                    ? 'WiFi'
                                    : null}
                                {rowData.transfer_mode === TRANSFER_MODE.lora
                                    ? 'LoRa'
                                    : null}
                                {rowData.transfer_mode ===
                                TRANSFER_MODE.cellular
                                    ? locale.devices.cellular
                                    : null}
                            </div>
                        </div>
                    )
                } else {
                    return (
                        <OperationModeIcon
                            operationMode={rowData.operation_mode}
                            locale={locale}
                        />
                    )
                }
            }
        },
        {
            key: 'name',
            label: locale.common.name,
            sortable: true,
            dataType: 'string',
            width: 1.5,
            minWidth: 140,
            render: ({ rowData, cellData }) => {
                const isAssigned = rowData.is_assigned
                return (
                    <Typography noWrap variant="inherit">
                        <Link
                            onClick={handleLinkClick}
                            className={classNames(classes.nameLink)}
                            to={`/devices/${rowData.id}`}>
                            <Highlighted
                                text={rowData.name}
                                highlight={search}
                            />
                        </Link>
                    </Typography>
                )
            }
        },
        {
            key: 'serial_number',
            label: locale.locations.deviceSerial,
            sortable: true,
            dataType: 'string',
            width: 1,
            minWidth: 140,
            render: ({ rowData, cellData }) => (
                <Link
                    onClick={handleLinkClick}
                    className={classNames(classes.nameLink)}
                    to={`/devices/${rowData.id}`}>
                    <Highlighted
                        text={rowData.serial_number}
                        highlight={search}
                    />
                </Link>
            )
        },
        {
            key: 'pest_control_system_id',
            label: locale.devices.pestControlSystemLabel,
            sortable: false,
            dataType: 'string',
            width: 1.5,
            minWidth: pestControlSystemWidth,
            maxWidth: pestControlSystemWidth,
            render: ({ rowData }) => {
                const systemName =
                    locale.devices.pestControlSystems[
                        rowData.pest_control_system_id
                    ] || 'N/A'
                return (
                    <Typography noWrap variant="inherit">
                        <Tooltip hover placement="top" text={systemName}>
                            {systemName}
                        </Tooltip>
                    </Typography>
                )
            }
        },
        {
            key: 'device_type',
            label: locale.devices.deviceType,
            sortable: true,
            dataType: 'string',
            width: 1.5,
            minWidth: 130,
            render: ({ rowData }) => {
                return (
                    <Typography noWrap variant="inherit">
                        {locale.devices.deviceTypes[rowData.device_type] ||
                            rowData.device_type}
                    </Typography>
                )
            }
        },
        {
            key: 'description',
            label: locale.common.description,
            sortable: false,
            dataType: 'string',
            width: 1.5,
            minWidth: 300,
            render: ({ rowData, cellData }) => (
                <Typography noWrap variant="inherit">
                    {rowData.description ? (
                        <Tooltip
                            hover
                            placement="top"
                            text={rowData.description}>
                            {rowData.description}
                        </Tooltip>
                    ) : (
                        '--'
                    )}
                </Typography>
            )
        },
        {
            key: 'location_name',
            label: locale.devices.location,
            sortable: true,
            dataType: 'string',
            width: 1.5,
            minWidth: 200,
            render: ({ rowData, cellData }) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {!rowData.is_assigned && (
                        <IsAssignedIcon
                            theme={theme}
                            locale={locale}
                            isLocked
                            isHidden={
                                !rowData.is_assigned &&
                                UserPermissions.isAllOriginalGranted()
                            }
                        />
                    )}
                    <Typography noWrap variant="inherit">
                        {isDisabledRow(rowData.is_assigned) ? (
                            <div>{rowData.location_name}</div>
                        ) : (
                            <Link
                                onClick={handleLinkClick}
                                className={classNames(classes.nameLink)}
                                to={`/locations/${rowData.location_id}/overview`}>
                                {rowData.location_name}
                            </Link>
                        )}
                    </Typography>
                </div>
            )
        },
        /* {
            key: 'customer_name',
            label: locale.devices.organisation,
            sortable: true,
            dataType: 'string',
            width: 1.5,
            render: ({ rowData, cellData }) => (
                rowData.customer_name
            )
        }, */
        {
            key: 'last_report_type',
            label: locale.devices.status,
            sortable: true,
            dataType: 'number',
            // alignCenter: true,
            width: 1.5,
            maxWidth: 150,
            minWidth: 150,
            render: ({ rowData }) => {
                const statusTooltipText = getDeviceStatusTooltipText(locale, {
                    report_type: rowData.last_report_type,
                    data: rowData
                })
                const isStatusIconWithTooltip = statusTooltipText !== null
                return (
                    <div className={classes.statusWrapper}>
                        {rowData.in_maintenance_mode ? (
                            <Tooltip
                                hover
                                placement="top"
                                text={locale.common.maintenanceMode}>
                                <Icon
                                    path={mdiTools}
                                    size={0.8}
                                    color={theme.primary}
                                />
                            </Tooltip>
                        ) : null}
                        <Tooltip
                            hover
                            hideTooltip={!isStatusIconWithTooltip}
                            placement="top"
                            text={statusTooltipText}>
                            <DeviceStatus value={rowData.last_report_type} />
                            {!embeded ? (
                                <span className={classes.deviceStatusShortText}>
                                    {getDeviceShortStatusText(rowData, locale)}
                                </span>
                            ) : null}
                        </Tooltip>
                    </div>
                )
            }
        },
        {
            key: 'qr_code',
            label: locale.devices.qrCode,
            sortable: false,
            dataType: 'string',
            alignCenter: true,
            width: 0.5,
            maxWidth: 70,
            minWidth: 70,
            render: ({ rowData, cellData }) => (
                <div>
                    <Icon
                        path={mdiQrcodeScan}
                        size={0.8}
                        color={theme.primary}
                        className={classes.qrIcon}
                        onClick={e => {
                            e.stopPropagation()
                            onQrIconClick(rowData['serial_number'])
                        }}
                    />
                </div>
            )
        },
        {
            key: 'battery_status',
            label: locale.devices.batteryStatus,
            sortable: true,
            dataType: 'number',
            alignCenter: true,
            width: 0.5,
            maxWidth: 100,
            minWidth: 100,
            render: ({ rowData, cellData }) => (
                <div className={classes.batteryWrapper}>
                    <div style={{ width: '50%', marginRight: 2 }}>
                        {`${getBatteryPercentage(rowData.battery_status)}% `}
                    </div>
                    <Battery value={rowData.battery_status} />
                </div>
            )
        },
        {
            key: 'last_heartbeat',
            label: locale.billing.lastHeartbeat,
            sortable: true,
            dataType: 'date',
            width: 1.5,
            minWidth: 150,
            alignCenter: true,
            render: ({ rowData }) => {
                return (
                    <Typography noWrap variant="inherit">
                        {getFormattedDate(rowData.last_heartbeat) || '--'}
                    </Typography>
                )
            }
        },
        {
            key: 'connected_gateway',
            label: locale.devices.connectedGateway,
            sortable: false,
            dataType: 'string',
            width: 1.5,
            minWidth: 150,
            // headerClasses: classes.lastColumn,
            alignCenter: true,
            render: ({ rowData }) => {
                const loraGatewayId =
                    rowData?.transfer_mode === TRANSFER_MODE.lora
                        ? rowData?.data?.LoRa?.gw_id
                        : undefined
                if (
                    loraGatewayId &&
                    typeof loraGatewayId === 'string' &&
                    loraGatewayId.length > 0
                ) {
                    return (
                        <Typography noWrap variant="inherit">
                            {loraGatewayId.toUpperCase()}
                        </Typography>
                    )
                } else {
                    return '--'
                }
            }
        },
        {
            key: 'wifi',
            label: locale.devices.wifiSignalStrength,
            sortable: false,
            dataType: 'string',
            width: 1.1,
            maxWidth: 140,
            minWidth: 140,
            headerClasses: classes.wifiWrapper,
            render: ({ rowData, cellData }) => (
                <div className={classes.wifiWrapper}>
                    <Tooltip
                        tooltipClass={classes.wifiTooltipWrapper}
                        hover
                        placement="top"
                        text={`${
                            locale.devices.wifiSignalStrengthHover
                        }: \n ${formatHeartbeatTimestamp(
                            rowData.last_heartbeat
                        )}`}>
                        <WifiStrengthIndicator
                            locale={locale}
                            theme={theme}
                            device={rowData}
                        />
                    </Tooltip>
                </div>
            )
        },
        ...(isDevicesToMoveMode
            ? [
                  {
                      key: 'actions',
                      label: '',
                      sortable: false,
                      dataType: 'custom',
                      alignCenter: true,
                      width: 0.5,
                      minWidth: 70,
                      render: ({ rowData, cellData }) => (
                          <IconButton
                              size="small"
                              className={classes.restoreIconButton}
                              onClick={e => {
                                  e.stopPropagation()
                                  onRestoreDevice(rowData.id)
                              }}>
                              <BackspaceIcon />
                          </IconButton>
                      )
                  }
              ]
            : []),
        /* ...(showAssignmentColumn
            ? [
                    {
                        key: 'is_assigned',
                        label: locale.common.assignment,
                        sortable: false,
                        dataType: 'custom',
                        alignCenter: true,
                        width: 0.5,
                        render: ({ rowData, cellData }) => {
                            return (
                                <div>
                                    <IsAssignedIcon theme={theme} locale={locale} isLocked={!rowData.is_assigned}/>
                                </div>
                            )
                        }
                    }
              ]
            : []), */
        {
            key: 'assigment_row_blocker',
            label: '',
            sortable: false,
            dataType: 'custom',
            alignCenter: true,
            width: 0,
            minWidth: 0,
            render: ({ rowData, cellData }) => {
                if (isDisabledRow(rowData.is_assigned))
                    return <div className={classes.assignmentRowBlocker} />
                return null
            }
        }
    ]

    const navigateToDevice = rowObj => {
        history.push(`/devices/${rowObj.rowData.id}`)
    }

    return (
        <TableVirtualized
            data={devices}
            isLoadingMore={isDevicesToMoveMode ? null : isLoadingMore}
            isLoading={isLoading}
            onLoadMore={isDevicesToMoveMode ? null : onLoadMore}
            columns={columns}
            sorting={sorting}
            onSort={onSort}
            onRowClick={navigateToDevice}
            noRowLeftPadding={multiselect && !isDevicesToMoveMode}
        />
    )
}

const DevicesTable = withRouter(DevicesTableComponent)
export { DevicesTable }
